import React, { useContext, useEffect, useState, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userService } from '@/services/userService';

//Material UI
import Toolbar from '@mui/material/Toolbar';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Card from '@mui/material/Card';

//Material-UI Icons
import PersonIcon from '@mui/icons-material/Person';

//Custom components
import FormContent from '@/components/FormContent';
import { GridContainer, GridItem } from '@/ui/Grid';
import {
  CardActionButtonActivate,
  CardActionButtonDelete,
  CardActionButtonSave,
  CardHeading,
  CardToolbarButtonBack,
  CardToolbarMenuButton,
  CardToolbarMenuItemActivate,
  CardToolbarMenuItemDeactivate,
  CardToolbarMenuItemDelete,
} from '@/ui/Card';
import { FormAvatar, FormHeading } from '@/ui/Form';
import useFormController from '@/components/useFormController';
import { FormContentMode } from '@/@types/components/formContent';
import { SnackbarContextType } from '@/@types/ui/SnackbarContext';
import dataController from '@/lib/dataController';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import FormFillContent from '@/ui/Form/FormFillContent';
import FormHeadingID from '@/ui/Form/FormHeadingId';

interface IUserFormProps {
  dc: dataController;
  recordId: number;
  mode: FormContentMode;
  form: string;
}

function UserForm(props: IUserFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;

  const { dc, recordId, mode, form } = props;
  const {
    record,
    validation,
    fields,
    onFieldChange,
    doUpdate,
    doDelete,
    doRefresh,
    doClose,
    dataChanged,
  } = useFormController({
    mode,
    recordId: recordId,
    form,
    dc,
    onClose: close,
  });

  const handleClickBack = () => {
    if (doClose) {
      doClose()
        .then((resp) => {
          if (resp.success) {
            navigate('/users');
          } else if (resp.shouldSave) {
            if (doUpdate) {
              doUpdate()
                .then((result) => {
                  if (result.success) {
                    const msg = 'messages.update_user_success';
                    snackbarContext.showNotification(msg, 'success');
                    navigate('/users');
                  }
                })
                .catch((result) => {
                  if (
                    result.success === false &&
                    result.validationPass === false
                  ) {
                    //do nothing
                  } else {
                    const msg = 'error.update_user';
                    snackbarContext.showNotification(msg, 'error');
                  }
                });
            }
          }
        })
        .catch((result) => {
          snackbarContext.showNotification(result.error, 'error');
        });
    }
  };

  const handleUpdate = (evt: any) => {
    if (doUpdate) {
      //activate or deactivate user if needed
      if (Array.isArray(record.active)) {
        if (record.active[0]) handleActivate()
        else handleDeactivate()
      }

      //update user
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.update_user_success';
            snackbarContext.showNotification(msg, 'success');
          }
        })
        .catch((result) => {
          if (result.success === false && result.validationPass === false) {
            //do nothing
          } else {
            const msg = 'error.update_user';
            snackbarContext.showNotification(msg, 'error');
          }
        });
    }
  };

  const handleDelete = (evt: any) => {
    if (doDelete) {
      doDelete()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.delete_user_success';
            snackbarContext.showNotification(msg, 'success');
            navigate('/users');
          }
        })
        .catch((result) => {
          const msg = 'error.delete_user';
          snackbarContext.showNotification(msg, 'error');
        });
    }
  };

  const handleActivate = () => {
    if (record.id !== null) {
      try {
        userService.activate(Number(record.id)).then((result) => {
          //@ts-ignore
          if (result.success) {
            doRefresh();
          } else {
            const msg = 'error.activate_user';
            snackbarContext.showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = 'error.activate_user';
        snackbarContext.showNotification(msg, 'error');
      }
    }
  };

  const handleDeactivate = () => {
    if (record.id !== null) {
      try {
        userService.deactivate(Number(record.id)).then((result) => {
          //@ts-ignore
          if (result.success) {
          } else {
            const msg = 'error.deactiavate_user';
            snackbarContext.showNotification(msg, 'error');
          }
        });
      } catch (error) {
        const msg = 'error.deactivate_user';
        snackbarContext.showNotification(msg, 'error');
      }
      navigate('/users/');
    }
  };


  const commonProps = {
    record: record,
    validation: validation,
    onFieldChange: onFieldChange,
    fields: fields,
    mode: mode,
    //pickerDataControllers: pickerDataControllers,
  };

  return (
    <Card>
      <CardHeading>
        <Toolbar variant="dense" disableGutters={true}>
          <FormAvatar ariaLabel="user" icon={PersonIcon} />
          <FormHeading
            header={record.first_name as string + " " + record.last_name as string}
            subheader={<FormHeadingID id={recordId} />}
            dataChanged={dataChanged} lowercase={false} children={undefined} />
          <FormFillContent />
          <CardToolbarButtonBack onClick={handleClickBack} />
          <CardToolbarMenuButton>
            {record.active ? (
              <CardToolbarMenuItemDeactivate onClick={handleDeactivate} />
            ) : (
              <CardToolbarMenuItemActivate onClick={handleActivate} />
            )}
            {record.user_type_id !== 2 ? <CardToolbarMenuItemDelete onClick={handleDelete} /> : null}
          </CardToolbarMenuButton>
        </Toolbar>
      </CardHeading>
      <CardContent style={{ paddingTop: 0 }}>
        <GridContainer direction="row" spacing={3}>
          <GridItem>
            <GridContainer direction="row">
              <FormContent
                title=' '
                fieldNames={[
                  'first_name',
                  'last_name',
                  'username',
                  'email',
                  'user_role',
                  'status',
                  'active'
                ]}
                {...commonProps}
                columns={2}
                mode={record.active ? 'update' : 'view'}
              />
            </GridContainer>
          </GridItem>
        </GridContainer>
      </CardContent>
      <CardActions>
        <FormFillContent />
        <CardActionButtonSave
          onClick={handleUpdate}
          variant="contained"
          disabled={!dataChanged}
        />
      </CardActions>
    </Card>
  );
}

export default UserForm;
